import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ClientStoreService } from './client-store.service';

// Interface for GPT prompts - matches backend schema
export interface GptPrompt {
  strId: string;
  strLabel: string;
  strDescription?: string;
  strType: string;
  strPromptTemplate: string;
  bIsActive: boolean;
  bIsSystem?: boolean; // System prompt flag (not deletable)
  dtCreated: string;
  dtUpdated: string;
  // Direct word_count field (backend saves it here)
  word_count?: number;
}

// Interface for creating new prompts
export interface GptPromptCreateRequest {
  strLabel: string;
  strDescription?: string;
  strType: string;
  word_count: number;
  strPromptTemplate: string;
  bIsActive?: boolean; // Added for update operations
}

// Interface for API responses
export interface GptPromptResponse {
  success?: boolean;
  data?: GptPrompt | GptPrompt[]; // Can be single prompt or array
  message?: string;
}



@Injectable({
  providedIn: 'root'
})
export class GptPromptService {
  // Disable verbose logging in production; flip to true locally if needed
  private readonly DEBUG: boolean = false;

  // Base URL for API calls - admin endpoints
  private baseUrl = environment.url + '/admin/gpt-prompts'

  constructor(
    private http: HttpClient,
    private clientStoreService: ClientStoreService
  ) {}

  /**
   * Transform backend prompt data to frontend format
   * Backend schema: id, name, module, prompt_template, word_count, is_active, etc.
   */
  private transformBackendToFrontend(backendPrompt: any): GptPrompt {

    return {
      strId: (backendPrompt.id && backendPrompt.id.toString()) || backendPrompt.strId,
      strLabel: backendPrompt.name || backendPrompt.strLabel,
      strDescription: backendPrompt.description || backendPrompt.strDescription,
      strType: backendPrompt.module || backendPrompt.strType,
      word_count: backendPrompt.word_count || 150,
      strPromptTemplate: backendPrompt.prompt_template || backendPrompt.strPromptTemplate,
      bIsActive: backendPrompt.is_active !== false,
      bIsSystem: backendPrompt.is_system || backendPrompt.bIsSystem || false, // System prompt flag
      dtCreated: backendPrompt.created_at || backendPrompt.dtCreated,
      dtUpdated: backendPrompt.updated_at || backendPrompt.dtUpdated
    };
  }

  /**
   * Get all GPT prompts from the backend
   */
  getPrompts(strModule?: string): Observable<GptPrompt[]> {
    // Backend expects module parameter in request body for filtering
    const body: any = {};
    if (strModule) {
      body.module = strModule;
    }

    return this.http.post<GptPromptResponse>(this.baseUrl, body).pipe(
      map(response => {

        if (response.success && response.data) {
          // Handle both single prompt and array responses
          const prompts = Array.isArray(response.data) ? response.data : [response.data];

          const transformed = prompts.map(backendPrompt => this.transformBackendToFrontend(backendPrompt));

          return transformed;
        }

        return [];
      }),
      catchError(error => {
        // Keep error in console in dev, suppress details in prod
        this.DEBUG && console.error('❌ Error fetching GPT prompts:', error);
        // Do not fallback to mock data; show empty list so UI can display placeholder
        return of([]);
      })
    );
  }

  /**
   * Get a specific prompt by ID
   */
  getPromptById(strId: string): Observable<GptPrompt | null> {
    // This method is no longer needed as mock data is removed.
    // If a prompt needs to be fetched by ID, it should be done via getPrompts
    // or a direct API call if the backend supports it.
    console.warn('getPromptById is deprecated as mock data is removed. Use getPrompts instead.');
    return of(null); // Return null as mock data is removed
  }

  /**
   * Create a new GPT prompt
   */
  createPrompt(request: GptPromptCreateRequest): Observable<GptPromptResponse> {
    // Let the auth interceptor handle authentication automatically
    // Don't set Content-Type header - let the interceptor add client_id and Authorization

    // Prepare backend request with correct field names
    const backendRequest = {
      strLabel: request.strLabel,
      strDescription: request.strDescription || '',
      strType: request.strType,
      strPromptTemplate: request.strPromptTemplate,
      word_count: request.word_count,
      bIsActive: request.bIsActive !== undefined ? request.bIsActive : true
    };

    return this.http.post<GptPromptResponse>(`${this.baseUrl}/store`, backendRequest).pipe(
      tap(response => {
      }),
      catchError(error => {
        this.DEBUG && console.error('Error creating GPT prompt:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing GPT prompt
   * Note: strType is NOT included - module type cannot be changed after creation
   */
  updatePrompt(id: string, request: GptPromptCreateRequest): Observable<GptPromptResponse> {
    // Create a clean request object WITHOUT strType for updates
    const backendRequest: any = {
      strLabel: request.strLabel,
      strDescription: request.strDescription || '',
      strPromptTemplate: request.strPromptTemplate,
      word_count: request.word_count
    };



    // CRITICAL: Ensure strType is NEVER included in update requests
    if (backendRequest.strType) {
      delete backendRequest.strType;
    }

    const url = `${this.baseUrl}/${id}/update`;

    return this.http.post<GptPromptResponse>(url, backendRequest).pipe(
      tap(_ => { }),
      catchError(error => { this.DEBUG && console.error('Error updating GPT prompt:', error); throw error; })
    );
  }

  /**
   * Delete a GPT prompt permanently (use POST /{id}/delete so interceptor appends auth fields)
   */
  deletePrompt(id: string): Observable<GptPromptResponse> {
    return this.http.post<GptPromptResponse>(`${this.baseUrl}/${id}/delete`, {}).pipe(
      map(response => response),
      catchError(error => {
        this.DEBUG && console.error('Error deleting GPT prompt:', error);
        throw error;
      })
    );
  }

  /**
   * Toggle active status of a GPT prompt
   */
  togglePromptActive(id: string): Observable<GptPromptResponse> {
    // Let the auth interceptor handle authentication automatically
    // Don't set Content-Type header - let the interceptor add client_id and Authorization
    return this.http.post<GptPromptResponse>(`${this.baseUrl}/${id}/toggle-active`, {}).pipe(
      tap(response => {
      }),
      catchError(error => {
        this.DEBUG && console.error('Error toggling GPT prompt status:', error);
        throw error;
      })
    );
  }

  /**
   * Refresh a system prompt to the latest default template for its module
   */
  refreshPrompt(id: string, module: string): Observable<GptPrompt> {
    const url = `${this.baseUrl}/${id}/refresh`;
    return this.http.post<GptPromptResponse>(url, { module }).pipe(
      map(response => {
        const backend = (response && response.data) as any;
        return this.transformBackendToFrontend(backend);
      })
    );
  }
}
